import { supabase } from '../supabaseClient';
import type { DailyNoteCategoryRow } from '../types/dailyRecordNote';
import { DEFAULT_DAILY_CATEGORY_COLOR } from '../types/dailyRecordNote';
import {
  buildDailyNoteCategoriesVersion,
  clearDailyNoteCategoriesCache,
  loadDailyNoteCategoriesCache,
  saveDailyNoteCategoriesCache,
} from '../utils/dailyNoteCategoriesCache';
import { clearDailyNotesListCache } from '../utils/dailyRecordNotesCache';
import {
  canEditDailyCategoryColor,
  isBuiltInDailyCategory,
  isDailyNoteSystemKey,
  isGeneralDailyCategory,
  planBuiltInDailyCategories,
} from '../utils/dailyNoteCategoryEdit';

let sessionCats: {
  userId: string;
  version: string;
  categories: DailyNoteCategoryRow[];
} | null = null;

function mapCategoryRow(row: Record<string, unknown>): DailyNoteCategoryRow {
  return {
    id: String(row.id ?? ''),
    user_id: String(row.user_id ?? ''),
    name: typeof row.name === 'string' ? row.name : '',
    color: typeof row.color === 'string' ? row.color : DEFAULT_DAILY_CATEGORY_COLOR,
    sort_order: typeof row.sort_order === 'number' ? row.sort_order : 0,
    created_at: String(row.created_at ?? ''),
    updated_at: String(row.updated_at ?? ''),
    system_key: isDailyNoteSystemKey(row.system_key) ? row.system_key : null,
  };
}

function sortCategories(rows: DailyNoteCategoryRow[]): DailyNoteCategoryRow[] {
  return [...rows].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
}

function rememberSession(userId: string, version: string, categories: DailyNoteCategoryRow[]): void {
  sessionCats = { userId, version, categories };
}

export function clearDailyNoteCategoriesRuntimeState(userId?: string): void {
  if (!userId || sessionCats?.userId === userId) sessionCats = null;
}

function versionOf(categories: DailyNoteCategoryRow[]): string {
  const maxUpdatedAt = categories.reduce((acc, c) => (c.updated_at > acc ? c.updated_at : acc), '');
  return buildDailyNoteCategoriesVersion(categories.length, maxUpdatedAt);
}

async function fetchCategoriesHead(userId: string): Promise<{ version: string; totalCount: number }> {
  const { data, error, count } = await supabase
    .from('daily_note_categories')
    .select('updated_at', { count: 'exact' })
    .eq('user_id', userId)
    .order('updated_at', { ascending: false })
    .limit(1);
  if (error) throw error;
  const totalCount = count ?? 0;
  const maxUpdatedAt =
    totalCount > 0 && data?.[0]
      ? String((data[0] as { updated_at?: string }).updated_at ?? '')
      : '';
  return {
    version: buildDailyNoteCategoriesVersion(totalCount, maxUpdatedAt),
    totalCount,
  };
}

async function fetchCategoryRows(userId: string): Promise<DailyNoteCategoryRow[]> {
  const { data, error } = await supabase
    .from('daily_note_categories')
    .select('id, user_id, name, color, sort_order, created_at, updated_at, system_key')
    .eq('user_id', userId)
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return sortCategories(
    ((data ?? []) as Record<string, unknown>[]).map(mapCategoryRow).filter((c) => c.id && c.name)
  );
}

export function peekDailyNoteCategoriesCache(userId: string): DailyNoteCategoryRow[] | null {
  if (!userId) return null;
  if (sessionCats?.userId === userId) return sessionCats.categories;
  const cached = loadDailyNoteCategoriesCache(userId);
  if (!cached) return null;
  rememberSession(userId, cached.version, cached.categories);
  return cached.categories;
}

async function persistCategoryRow(row: DailyNoteCategoryRow): Promise<DailyNoteCategoryRow> {
  const { data, error } = await supabase
    .from('daily_note_categories')
    .upsert(
      {
        id: row.id,
        user_id: row.user_id,
        name: row.name,
        color: row.color,
        sort_order: row.sort_order,
        created_at: row.created_at,
        updated_at: row.updated_at,
        system_key: row.system_key,
      },
      { onConflict: 'id' }
    )
    .select('id, user_id, name, color, sort_order, created_at, updated_at, system_key')
    .single();
  if (error) throw error;
  return mapCategoryRow(data as Record<string, unknown>);
}

async function ensureBuiltInCategories(
  userId: string,
  rows: DailyNoteCategoryRow[]
): Promise<DailyNoteCategoryRow[]> {
  const planned = planBuiltInDailyCategories({
    categories: rows,
    userId,
    generateId: () => crypto.randomUUID(),
    nowIso: new Date().toISOString(),
  });
  let next = planned.categories;
  for (const row of [...planned.patches, ...planned.inserts]) {
    const saved = await persistCategoryRow(row);
    next = next.map((c) => (c.id === row.id || c.system_key === saved.system_key ? saved : c));
    if (!next.some((c) => c.id === saved.id)) next = [...next, saved];
  }
  const fallback = next.find((c) => isGeneralDailyCategory(c));
  for (const row of planned.removes) {
    if (fallback) {
      const { error: reassignError } = await supabase
        .from('daily_record_notes')
        .update({ category_id: fallback.id })
        .eq('user_id', userId)
        .eq('category_id', row.id);
      if (reassignError) throw reassignError;
    }
    await persistCategoryRow({ ...row, system_key: null, updated_at: new Date().toISOString() });
    const { error } = await supabase
      .from('daily_note_categories')
      .delete()
      .eq('user_id', userId)
      .eq('id', row.id);
    if (error) throw error;
    next = next.filter((c) => c.id !== row.id);
  }
  return sortCategories(next);
}

async function backfillNullCategoryNotes(userId: string, defaultId: string): Promise<boolean> {
  const { data, error: peekError } = await supabase
    .from('daily_record_notes')
    .select('id')
    .eq('user_id', userId)
    .is('category_id', null)
    .limit(1);
  if (peekError) throw peekError;
  if (!data?.length) return false;
  const { error } = await supabase
    .from('daily_record_notes')
    .update({ category_id: defaultId })
    .eq('user_id', userId)
    .is('category_id', null);
  if (error) throw error;
  return true;
}

function cacheCategories(userId: string, categories: DailyNoteCategoryRow[]): DailyNoteCategoryRow[] {
  const sorted = sortCategories(categories);
  const version = versionOf(sorted);
  rememberSession(userId, version, sorted);
  saveDailyNoteCategoriesCache(userId, { version, categories: sorted });
  return sorted;
}

/**
 * 카테고리 목록. LS 즉시 표시 후 head 비교. 기본 데일리·기본쪽지가 없으면 만든다.
 */
export async function fetchDailyNoteCategories(
  userId: string,
  options?: { forceRefresh?: boolean }
): Promise<DailyNoteCategoryRow[]> {
  if (!userId) return [];

  if (!options?.forceRefresh) {
    const cached = peekDailyNoteCategoriesCache(userId);
    if (cached && sessionCats?.userId === userId) {
      const head = await fetchCategoriesHead(userId);
      if (head.version === sessionCats.version && head.totalCount > 0) {
        const planned = planBuiltInDailyCategories({
          categories: sessionCats.categories,
          userId,
          generateId: () => crypto.randomUUID(),
          nowIso: new Date().toISOString(),
        });
        if (planned.inserts.length === 0 && planned.patches.length === 0 && planned.removes.length === 0) {
          return sessionCats.categories;
        }
      }
    }
  }

  let rows = await ensureBuiltInCategories(userId, await fetchCategoryRows(userId));
  const defaultRow = rows.find((c) => isGeneralDailyCategory(c)) ?? rows[0];
  const backfilled = defaultRow ? await backfillNullCategoryNotes(userId, defaultRow.id) : false;
  if (backfilled) clearDailyNotesListCache(userId);
  return cacheCategories(userId, rows);
}

export async function saveDailyNoteCategory(
  userId: string,
  category: Pick<DailyNoteCategoryRow, 'id' | 'name' | 'color' | 'sort_order' | 'system_key'>
): Promise<DailyNoteCategoryRow> {
  if (!userId) throw new Error('로그인이 필요합니다.');
  const now = new Date().toISOString();
  const prev = peekDailyNoteCategoriesCache(userId) ?? [];
  const existing = prev.find((c) => c.id === category.id);
  const nextColor =
    existing && !canEditDailyCategoryColor(existing) ? existing.color : category.color;
  const { data, error } = await supabase
    .from('daily_note_categories')
    .upsert(
      {
        id: category.id,
        user_id: userId,
        name: category.name,
        color: nextColor,
        sort_order: category.sort_order,
        updated_at: now,
        system_key: existing?.system_key ?? category.system_key ?? null,
      },
      { onConflict: 'id' }
    )
    .select('id, user_id, name, color, sort_order, created_at, updated_at, system_key')
    .single();
  if (error) throw error;
  const row = mapCategoryRow(data as Record<string, unknown>);
  const next = prev.some((c) => c.id === row.id)
    ? prev.map((c) => (c.id === row.id ? row : c))
    : [...prev, row];
  cacheCategories(userId, next);
  return row;
}

export async function saveDailyNoteCategoryOrder(
  userId: string,
  orderedIds: string[]
): Promise<DailyNoteCategoryRow[]> {
  const prev = peekDailyNoteCategoriesCache(userId) ?? [];
  const now = new Date().toISOString();
  const next = prev.map((c) => {
    const idx = orderedIds.indexOf(c.id);
    if (idx < 0) return c;
    return { ...c, sort_order: idx, updated_at: now };
  });
  const rows = orderedIds
    .map((id, sort_order) => {
      const c = next.find((x) => x.id === id);
      if (!c) return null;
      return {
        id: c.id,
        user_id: userId,
        name: c.name,
        color: c.color,
        sort_order,
        updated_at: now,
        system_key: c.system_key,
      };
    })
    .filter(Boolean);
  if (rows.length > 0) {
    const { error } = await supabase.from('daily_note_categories').upsert(rows, { onConflict: 'id' });
    if (error) throw error;
  }
  return cacheCategories(userId, next);
}

export async function deleteDailyNoteCategory(userId: string, categoryId: string): Promise<DailyNoteCategoryRow[]> {
  if (!userId || !categoryId) return peekDailyNoteCategoriesCache(userId) ?? [];
  const prev = peekDailyNoteCategoriesCache(userId) ?? [];
  const target = prev.find((c) => c.id === categoryId);
  if (!target || isBuiltInDailyCategory(target)) return prev;

  const defaultRow = prev.find((c) => isGeneralDailyCategory(c));
  if (defaultRow) {
    const { error: reassignError } = await supabase
      .from('daily_record_notes')
      .update({ category_id: defaultRow.id })
      .eq('user_id', userId)
      .eq('category_id', categoryId);
    if (reassignError) throw reassignError;
  }

  const { error } = await supabase
    .from('daily_note_categories')
    .delete()
    .eq('user_id', userId)
    .eq('id', categoryId);
  if (error) throw error;

  return cacheCategories(
    userId,
    prev.filter((c) => c.id !== categoryId)
  );
}

export function invalidateDailyNoteCategories(userId: string): void {
  clearDailyNoteCategoriesRuntimeState(userId);
  clearDailyNoteCategoriesCache(userId);
}
